import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { roleBlocksPath } from "@/lib/nav";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { BottomNav } from "./BottomNav";
import { useAuth } from "@/context/AuthContext";
import { hasPerm as _hasPerm } from "@/lib/perms";

// URL-level guard for financial report pages (server enforces the same permissions).
function FinGate({ perms, children, label = "Financial reports are limited to owners and authorised roles." }) {
  const { user } = useAuth();
  if (perms.some((p) => _hasPerm(user, p))) return children;
  return (
    <div className="rounded-2xl border border-pawkt-border bg-white p-8 text-center" data-testid="no-access">
      <p className="font-display text-lg font-bold text-slate-900">You don't have access to this page</p>
      <p className="mt-1 text-sm text-slate-500">{label}</p>
    </div>
  );
}
// URL-level guard for role-scoped sidebars (server RBAC enforces the data access).
function RoleGate({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (!roleBlocksPath(user, pathname)) return children;
  return (
    <div className="rounded-2xl border border-pawkt-border bg-white p-8 text-center" data-testid="role-no-access">
      <p className="font-display text-lg font-bold text-slate-900">You don't have access to this page</p>
      <p className="mt-1 text-sm text-slate-500">This module isn't part of your role.</p>
    </div>
  );
}
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

import Dashboard from "@/pages/Dashboard";
import CommandCenter from "@/pages/CommandCenter";
import Customers from "@/pages/Customers";
import CustomerIntelligence from "@/pages/CustomerIntelligence";
import CustomerDetail from "@/pages/CustomerDetail";
import Pets from "@/pages/Pets";
import PetDetail from "@/pages/PetDetail";
import Leads from "@/pages/Leads";
import Services from "@/pages/Services";
import ServiceProfitability from "@/pages/ServiceProfitability";
import Bookings from "@/pages/Bookings";
import CalendarPage from "@/pages/CalendarPage";
import POS from "@/pages/POS";
import Invoices from "@/pages/Invoices";
import Payments from "@/pages/Payments";
import PaymentOps from "@/pages/PaymentOps";
import Staff from "@/pages/Staff";
import Inventory from "@/pages/Inventory";
import OrderSoon from "@/pages/OrderSoon";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import BusinessSettings from "@/pages/BusinessSettings";
import ReceptionistSetup from "@/pages/ReceptionistSetup";
import ReceptionistPlans from "@/pages/ReceptionistPlans";
import ReceptionistReports from "@/pages/ReceptionistReports";
import CallIntelligence from "@/pages/CallIntelligence";
import ShallyOverview from "@/pages/ShallyOverview";
import ShallyFollowUps from "@/pages/ShallyFollowUps";
import { ShallyShell } from "@/components/shally/ShallyShell";
import ReceptionistKnowledge from "@/pages/ReceptionistKnowledge";
import ReceptionistOutbound from "@/pages/ReceptionistOutbound";
import BusinessCalling from "@/pages/BusinessCalling";
import OutboundActivation from "@/pages/OutboundActivation";
import ShallyLive from "@/pages/ShallyLive";
import DataQuality from "@/pages/DataQuality";
import DataImport from "@/pages/DataImport";
import InventoryImport from "@/pages/InventoryImport";
import SaasBilling from "@/pages/SaasBilling";
import { InvoiceSetupModal } from "@/components/pawkt/InvoiceSetupModal";
import { PlanLimitDialog } from "@/components/pawkt/PlanLimitDialog";
import { useTrialExpired, TrialExpiredScreen } from "@/components/billing/Trial";
import InvoiceSettings from "@/pages/InvoiceSettings";
import CommercialRules from "@/pages/CommercialRules";
import BusinessPlanning from "@/pages/BusinessPlanning";
import CommTemplates from "@/pages/CommTemplates";
import CommAutomation from "@/pages/CommAutomation";
import AutomationCenter from "@/pages/AutomationCenter";
import Memberships from "@/pages/Memberships";
import Packages from "@/pages/Packages";
import Coupons from "@/pages/Coupons";
import Loyalty from "@/pages/Loyalty";
import Segments from "@/pages/Segments";
import Communications from "@/pages/Communications";
import FollowUps from "@/pages/FollowUps";
import ActionCenter from "@/pages/ActionCenter";
import StaffPerformance from "@/pages/StaffPerformance";
import Grooming from "@/pages/Grooming";
import Team from "@/pages/Team";
import Roles from "@/pages/Roles";
import ApprovalCenter from "@/pages/ApprovalCenter";
import ApprovalSettings from "@/pages/ApprovalSettings";
import Veterinary from "@/pages/vertical/Veterinary";
import Boarding from "@/pages/vertical/Boarding";
import Training from "@/pages/vertical/Training";
import Finance from "@/pages/vertical/Finance";
import Expenses from "@/pages/Expenses";
import Accounts from "@/pages/Accounts";
import CaWorkspace from "@/pages/CaWorkspace";
import ControlCentre from "@/pages/ControlCentre";
import BusinessReview from "@/pages/BusinessReview";
import BusinessActions from "@/pages/BusinessActions";
import ExpenseImport from "@/pages/ExpenseImport";
import NotFound from "@/pages/NotFound";
import { ForcePasswordChange } from "@/components/pawkt/ForcePasswordChange";

const DESKTOP_MQ = "(min-width: 1024px)";

// Mount exactly one Sidebar: inline on desktop (lg+), in the drawer below lg.
function useIsDesktop() {
  const [desktop, setDesktop] = useState(() => window.matchMedia(DESKTOP_MQ).matches);
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_MQ);
    const onChange = (e) => setDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return desktop;
}

export function AppShell() {
  const { user } = useAuth();
  const [mobileNav, setMobileNav] = useState(false);
  const isDesktop = useIsDesktop();
  const trialExpired = useTrialExpired();

  // Temporary-password first login: nothing else renders until the password is changed (also enforced server-side).
  if (user && user.must_change_password) return <ForcePasswordChange />;
  // Paid-signup payment gate (server-authoritative flag) takes precedence over onboarding.
  if (user && user.signup_payment_required) {
    return <Navigate to="/checkout" replace />;
  }
  if (user && user.tenant && !user.tenant.onboarded) {
    return <Navigate to="/onboarding" replace />;
  }
  // Free Plan trial expired: the API already refuses every operational call (402); show only the upgrade flow.
  if (trialExpired) return <TrialExpiredScreen />;

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {isDesktop ? (
        <div className="hidden lg:block">
          <Sidebar />
        </div>
      ) : (
        <Sheet open={mobileNav} onOpenChange={setMobileNav}>
          <SheetContent side="left" className="p-0 w-[260px]">
            <SheetTitle className="sr-only">Navigation menu</SheetTitle>
            <Sidebar onNavigate={() => setMobileNav(false)} />
          </SheetContent>
        </Sheet>
      )}

      <div className="flex flex-1 flex-col overflow-hidden">
        <Topbar onMenu={() => setMobileNav(true)} />
        <main className="flex-1 overflow-y-auto p-4 pb-24 sm:p-6 sm:pb-24 lg:p-8 lg:pb-8">
          <div className="mx-auto max-w-[1400px] animate-fade-in">
            <RoleGate>
            <Routes>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/command-center" element={<CommandCenter />} />
              <Route path="/business-planning" element={<BusinessPlanning />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/customers/intelligence" element={<CustomerIntelligence />} />
              <Route path="/customers/:id" element={<CustomerDetail />} />
              <Route path="/pets" element={<Pets />} />
              <Route path="/pets/:id" element={<PetDetail />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/services" element={<Services />} />
              <Route path="/service-profitability" element={<FinGate perms={["reports.view"]}><ServiceProfitability /></FinGate>} />
              <Route path="/memberships" element={<Memberships />} />
              <Route path="/packages" element={<Packages />} />
              <Route path="/coupons" element={<Coupons />} />
              <Route path="/loyalty" element={<Loyalty />} />
              <Route path="/segments" element={<Segments />} />
              <Route path="/bookings" element={<Bookings />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/pos" element={<POS />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/payments" element={<Payments />} />
              <Route path="/approvals" element={<ApprovalCenter />} />
              <Route path="/approvals-settings" element={<ApprovalSettings />} />
              <Route path="/payment-ops" element={<PaymentOps />} />
              <Route path="/communications" element={<Communications />} />
              <Route path="/follow-ups" element={<FollowUps />} />
              <Route path="/action-center" element={<ActionCenter />} />
              <Route path="/automation" element={<AutomationCenter />} />
              <Route path="/staff-performance" element={<StaffPerformance />} />              <Route path="/communications/templates" element={<CommTemplates />} />
              <Route path="/communications/automation" element={<CommAutomation />} />
              <Route path="/invoice-settings" element={<InvoiceSettings />} />
              <Route path="/grooming" element={<Grooming />} />
              <Route path="/veterinary" element={<FinGate perms={["vet.view"]} label="The veterinary workspace is limited to clinic staff."><Veterinary /></FinGate>} />
              <Route path="/boarding" element={<Boarding />} />
              <Route path="/training" element={<Training />} />
              <Route path="/finance" element={<FinGate perms={["reports.view", "expenses.view"]}><Finance /></FinGate>} />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/accounts" element={<Accounts />} />
              <Route path="/ca" element={<CaWorkspace />} />
              <Route path="/control-centre" element={<ControlCentre />} />
              <Route path="/business-review" element={<FinGate perms={["reports.view", "expenses.view"]}><BusinessReview /></FinGate>} />
              <Route path="/business-actions" element={<FinGate perms={["reports.view", "expenses.view"]}><BusinessActions /></FinGate>} />
              <Route path="/expense-import" element={<ExpenseImport />} />
              <Route path="/staff" element={<Staff />} />
              <Route path="/data-quality" element={<DataQuality />} />
              <Route path="/data-import" element={<DataImport />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/order-soon" element={<OrderSoon />} />
              <Route path="/inventory-import" element={<InventoryImport />} />
              <Route path="/reports" element={<FinGate perms={["reports.view"]}><Reports /></FinGate>} />
              <Route path="/team" element={<FinGate perms={["settings.edit"]} label="Staff Management is limited to owners and admins with staff-management permission."><Team /></FinGate>} />
              <Route path="/roles" element={<Roles />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/business" element={<BusinessSettings />} />
              <Route path="/settings/ai-receptionist" element={<ShallyShell><FinGate perms={["settings.edit"]} label="AI Receptionist setup is limited to owners and admins."><ReceptionistSetup /></FinGate></ShallyShell>} />
              <Route path="/settings/ai-receptionist/plans" element={<ShallyShell><FinGate perms={["settings.edit"]} label="AI Receptionist plans are limited to owners and admins."><ReceptionistPlans /></FinGate></ShallyShell>} />
              <Route path="/settings/ai-receptionist/live" element={<ShallyShell><FinGate perms={["settings.edit"]} label="The Shally voice test is limited to owners and admins."><ShallyLive /></FinGate></ShallyShell>} />
              <Route path="/settings/ai-receptionist/knowledge" element={<ShallyShell><FinGate perms={["settings.edit"]} label="Business knowledge is limited to owners and admins."><ReceptionistKnowledge /></FinGate></ShallyShell>} />
              <Route path="/shally/business-calling/activate" element={<ShallyShell><FinGate perms={["settings.edit"]} label="Only owners and admins can activate outbound calling."><OutboundActivation /></FinGate></ShallyShell>} />
              <Route path="/shally/business-calling" element={<ShallyShell><FinGate perms={["calls.outbound"]} label="Shally business calling is limited to owners and admins."><BusinessCalling /></FinGate></ShallyShell>} />
              <Route path="/settings/ai-receptionist/outbound" element={<ShallyShell><FinGate perms={["calls.outbound"]} label="Outbound AI Calling is limited to owners and admins."><ReceptionistOutbound /></FinGate></ShallyShell>} />
              <Route path="/settings/ai-receptionist/reports" element={<ShallyShell><FinGate perms={["settings.view"]} label="AI Receptionist reports are limited to owners and admins."><ReceptionistReports /></FinGate></ShallyShell>} />
              <Route path="/shally" element={<ShallyShell><FinGate perms={["calls.view"]} label="Shally call history is limited to owners, admins and permitted managers."><ShallyOverview /></FinGate></ShallyShell>} />
              <Route path="/shally/calls" element={<ShallyShell><FinGate perms={["calls.view"]} label="Shally call history is limited to owners, admins and permitted managers."><CallIntelligence /></FinGate></ShallyShell>} />
              <Route path="/shally/follow-ups" element={<ShallyShell><FinGate perms={["calls.view"]} label="Shally follow-ups are limited to owners, admins and permitted managers."><ShallyFollowUps /></FinGate></ShallyShell>} />
              <Route path="/call-intelligence" element={<Navigate to="/shally/calls" replace />} />
              <Route path="/settings/commercial-rules" element={<CommercialRules />} />
              <Route path="/settings/billing" element={<SaasBilling />} />
              <Route path="/billing" element={<SaasBilling />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </RoleGate>
          </div>
        </main>
      </div>
      <BottomNav onMore={() => setMobileNav(true)} />
      <InvoiceSetupModal />
      <PlanLimitDialog />
    </div>
  );
}
